import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
} from "@chakra-ui/react";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { last, noop } from "lodash-es";
import React, { useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  generatePath,
  Link,
  Navigate,
  Outlet,
  resolvePath,
  UNSAFE_RouteContext,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  FormFooter,
  FormFooterActions,
  FormFooterBreadcrumb,
} from "shared/components/form/FormFooter";
import { isCurrentWizardPage } from "shared/components/layout/PageWizardUtils";
import { getRouteHandle } from "shared/navigation/RouteHandle";
import { RouteId } from "shared/navigation/Routes";
import { useRouteBasePath } from "shared/navigation/UseRouteBasePath";
import { throwExpression } from "shared/utils/ErrorUtils";
import { isSamePath } from "shared/utils/UrlUtils";

export interface WizardPage {
  id: string;
  title: string;
  breadcrumb?: string;
  path: string;
  resolvedPath: string;
}

export interface PageWizardProviderProps {
  allowNavigate?: boolean;
  onExit?: () => void;
  exitTitle?: string;
  finishTitle?: string;
  onFinish?: () => void;
  hiddenPages?: Partial<Record<RouteId, boolean>>;
}

export interface PageWizardContextValues {
  pages: WizardPage[];
  allowNavigate?: boolean;
  currentPage: WizardPage | undefined;
  nextPage?: WizardPage;
  prevPage?: WizardPage;
  currentIndex: number;
  navigateBack: () => void;
  navigateNext: () => void;
  exit?: () => void;
  exitTitle: string;
  finishTitle: string;
  canGoBack: boolean;
  canGoNext: boolean;
  canExit: boolean;
  basePath: string;
}

const PageWizardContext = React.createContext<PageWizardContextValues>(
  undefined as never,
);

export const usePageWizard = () => useContext(PageWizardContext);

export const PageWizardProvider: React.FC<
  React.PropsWithChildren<PageWizardProviderProps>
> = ({
  children,
  allowNavigate,
  hiddenPages,
  onExit,
  onFinish,
  exitTitle,
  finishTitle,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation(["common"]);

  const basePath = useRouteBasePath();

  // The last route here is the page containing the <PageWizard /> component and
  // its children are the wizard step pages.
  // FIXME: Find a safer way to do this.
  const { matches: matchesForTabsPage } = useContext(UNSAFE_RouteContext);

  const value = useMemo((): PageWizardContextValues => {
    const resolvedPages =
      last(matchesForTabsPage)
        ?.route.children?.filter(
          (route) =>
            !(hiddenPages && route.id && hiddenPages[route.id as RouteId]),
        )
        .map(
          (route): WizardPage => ({
            id: route.id!,
            path: route.path!,
            breadcrumb: getRouteHandle(route)?.breadcrumb,
            title:
              getRouteHandle(route)?.title ??
              throwExpression("Route title required"),
            resolvedPath: generatePath(
              route.path!.startsWith("/")
                ? route.path!
                : resolvePath(route.path!, basePath).pathname,
              params,
            ),
          }),
        ) ?? [];

    const currentIndex = resolvedPages.findIndex((page) =>
      isCurrentWizardPage(basePath, page.resolvedPath, location.pathname),
    );

    const currentPage = resolvedPages[currentIndex];
    const nextPage = resolvedPages[currentIndex + 1];
    const prevPage =
      currentIndex > 0 ? resolvedPages[currentIndex - 1] : undefined;

    const canGoBack = currentIndex > 0;
    const canGoNext = currentIndex < resolvedPages.length - 1;
    const canExit = Boolean(onExit);

    const navigateBack = () => {
      if (canGoBack) {
        navigate(resolvedPages[currentIndex - 1].path);
      }
    };

    const navigateNext = () => {
      if (canGoNext) {
        navigate(resolvedPages[currentIndex + 1].path);
      } else {
        onFinish?.();
      }
    };

    return {
      pages: resolvedPages,
      currentPage,
      currentIndex,
      nextPage,
      prevPage,
      navigateBack,
      navigateNext,
      allowNavigate,
      canGoBack,
      canGoNext,
      canExit,
      basePath,
      exit: onExit ?? noop,
      finishTitle: finishTitle ?? t("common:finish"),
      exitTitle: exitTitle ?? t("common:exit"),
    };
  }, [
    allowNavigate,
    basePath,
    exitTitle,
    finishTitle,
    hiddenPages,
    location.pathname,
    matchesForTabsPage,
    navigate,
    onExit,
    onFinish,
    params,
    t,
  ]);

  const isWizardBasePath = isSamePath(basePath, location.pathname);
  if (isWizardBasePath && !value.currentPage) {
    return <Navigate to={value.pages[0].resolvedPath} replace={true} />;
  }

  return (
    <PageWizardContext.Provider value={value}>
      {children}
    </PageWizardContext.Provider>
  );
};

export const PageWizardRoutes: React.FC<{
  context?: unknown;
}> = ({ context }) => (
  <QueryBoundary isPage={true}>
    <Outlet context={context} />
  </QueryBoundary>
);

export interface PageWizardBarProps {
  allowNavigate?: boolean;
}

export const PageWizardBar: React.FC<PageWizardBarProps> = ({
  allowNavigate: propsAllowNavigate,
}) => {
  const {
    pages,
    currentPage,
    allowNavigate: wizardAllowNavigate,
  } = usePageWizard();
  const allowNavigate = propsAllowNavigate ?? wizardAllowNavigate;
  return (
    <Breadcrumb
      separator={<ChevronRightIcon />}
      data-ls-enabled={true}
      data-public={true}
    >
      {pages.map(({ id, title, breadcrumb, resolvedPath }, idx) => {
        const pageTitle = breadcrumb ?? title;
        const isCurrentPage = currentPage?.id === id;
        return (
          <BreadcrumbItem key={idx} isCurrentPage={isCurrentPage}>
            {allowNavigate !== false && resolvedPath && !isCurrentPage ? (
              <BreadcrumbLink as={Link} to={resolvedPath}>
                {pageTitle}
              </BreadcrumbLink>
            ) : (
              <BreadcrumbLink aria-disabled={true}>{pageTitle}</BreadcrumbLink>
            )}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};
export const PageWizardFormFooter: React.FC<{
  onClickNext?: (() => Promise<void>) | (() => void);
  submitPermission?: PermissionTypeEnum;
}> = ({ onClickNext, submitPermission }) => {
  const { t } = useTranslation(["common"]);

  const hasForm = !!useFormContext() as boolean;

  const {
    nextPage,
    navigateBack,
    navigateNext,
    canGoBack,
    canExit,
    exit,
    exitTitle,
    finishTitle,
  } = usePageWizard();

  const nextTitle = nextPage?.title
    ? t("common:wizard.continueToX", {
        title: nextPage.breadcrumb ?? nextPage.title,
      })
    : finishTitle;

  return (
    <FormFooter>
      <FormFooterBreadcrumb>
        <PageWizardBar />
      </FormFooterBreadcrumb>
      <FormFooterActions>
        {canGoBack && (
          <Button colorScheme="secondary" onClick={navigateBack}>
            {t("common:back")}
          </Button>
        )}
        {canExit && (
          <Button colorScheme="secondary" onClick={exit}>
            {exitTitle}
          </Button>
        )}
        <Restricted permission={submitPermission || PermissionTypeEnum.read}>
          {hasForm ? (
            <FormSubmitButton>{nextTitle}</FormSubmitButton>
          ) : (
            <Button onClick={onClickNext ?? navigateNext}>{nextTitle}</Button>
          )}
        </Restricted>
      </FormFooterActions>
    </FormFooter>
  );
};
